import { useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useCustom } from "@refinedev/core";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  PawPrint,
  Pencil,
  Trash2,
  UserRound,
} from "lucide-react";

import { useAnimalPhotoUrls } from "../../api/useAnimalPhotoUrls";
import { formatEuro } from "../../i18n/format";
import type { PensionDier } from "../pensionKlantTypes";
import { dagenTussen, geslachtTeken, leeftijd, type Context } from "../pensionklant/hulp";
import { useLocatieUrl } from "./locatie";
import { AccommodatieIcoon, meervoud, type AccommodatieType, type Prijsberekening } from "../pensionplanning/accommodatie";
import { tarievenVoor, usePrijscategorieen, type Prijscategorie } from "./tarieven";
import { maakVerzoek, useBerekening, type Berekening } from "./aanvraag";
import {
  KlantBalk,
  Terug,
  Veld,
  Voet,
  Volgende,
  beschikbaarheidUrl,
  langeDatum,
  useKlantDieren,
  type Beschikbaarheid,
  type GekozenKlant,
  type Periode,
  type Vertaal,
} from "./bouwstenen";
import { EigenElementen, GEEN_ANTWOORDEN, NIETS, VerplichtMelding, VraagVeld, useVerplicht } from "./EigenElementen";
import {
  antwoordSleutel,
  blokAan,
  blokTitel,
  stapKop,
  standaardIndeling,
  type Antwoorden,
  type WizardElement,
  type WizardStap,
} from "./indeling";

/** Wat de balie per dier invult bij Verblijf &amp; verzorging. */
export interface DierVerblijf {
  typeId: number | null;
  /** Het pensiontarief waarmee gerekend wordt; leeg is dat van het dier zelf. */
  prijscategorieId: number | null;
  /** "STANDAARD", "EIGEN", of "categorie:&lt;id&gt;" voor voer van het pension. */
  voeding: string;
  voedingInstructies: string;
  medicatie: boolean;
  medicatieInstructies: string;
  /** Leeg: niet borstelen; anders het id van de borstelsoort. */
  borstelen: string;
  /** Leeg: niet wassen; anders het id van de wassoort. */
  wassen: string;
  samenMetGezin: boolean;
  bijzonderheden: string;
}

interface Opties {
  borstelen: { id: number; naam: string }[];
  wassen: { id: number; naam: string }[];
  voeding: { id: number; naam: string }[];
}

const TEKST_MAX = 500;
const STANDAARD = standaardIndeling().stappen.find((s) => s.soort === "VERBLIJF")!;

/** De verblijfstypes voor de diersoort van een dier; quarantaine kiest de balie niet. */
export function typenVoor(d: Pick<PensionDier, "diersoort">, typen: AccommodatieType[]): AccommodatieType[] {
  return typen.filter((x) => !x.quarantaine && (x.diersoort ?? "").toLowerCase() === (d.diersoort ?? "").toLowerCase());
}

/**
 * Wat een dier vooraf krijgt: het enige verblijf als er maar één is, standaard voeding, en medicatie en bijzonderheden
 * zoals ze bij het dier staan.
 */
export function standaardVerblijf(d: PensionDier, typen: AccommodatieType[]): DierVerblijf {
  const eigen = typenVoor(d, typen);
  return {
    typeId: eigen.length === 1 ? eigen[0].id : null,
    prijscategorieId: d.prijs?.categorieId ?? null,
    voeding: "STANDAARD",
    voedingInstructies: "",
    medicatie: (d.stand?.medicatieActief ?? 0) > 0,
    medicatieInstructies: d.stand?.medicatieNaam ?? "",
    borstelen: "",
    wassen: "",
    samenMetGezin: false,
    bijzonderheden: d.bijzonderheden ?? "",
  };
}

/**
 * Of een dier een tarief moet krijgen: het heeft geen eigen dagprijs en geen tarief, en er is er wel een voor zijn
 * soort. Zonder die keuze zou het verblijf op € 0,00 uitkomen.
 */
export function tariefNodig(d: PensionDier, tarieven: Prijscategorie[]): boolean {
  const eigenPrijs = (d.prijs?.dagtariefLaag ?? 0) > 0 || d.prijs?.categorieId != null;
  return !eigenPrijs && tarievenVoor(d, tarieven).length > 0;
}

/** Of elk dier is ingevuld, een verblijf heeft als er voor zijn soort een te kiezen is, en een prijs. */
export function verblijfCompleet(
  dieren: PensionDier[],
  typen: AccommodatieType[],
  verblijf: Record<number, DierVerblijf>,
  tarieven: Prijscategorie[] = [],
): boolean {
  return dieren.every(
    (d) =>
      verblijf[d.id] != null &&
      (typenVoor(d, typen).length === 0 || verblijf[d.id].typeId != null) &&
      (!tariefNodig(d, tarieven) || verblijf[d.id].prijscategorieId != null),
  );
}

/**
 * Stap Verblijf &amp; verzorging: per dier het type verblijf, de voeding, medicatie, verzorging en bijzonderheden, in
 * de volgorde en met de namen van de indeling, met de eigen vragen per dier erbij. Een dier kan hier nog van de
 * reservering af.
 */
export function VerblijfStap({
  klant,
  dierIds,
  setDierIds,
  periode: p,
  verblijf,
  setVerblijf,
  ctx,
  onKlant,
  onPeriode,
  onTerug,
  onVolgende,
  volgendeLabel,
  stap = STANDAARD,
  antwoorden = GEEN_ANTWOORDEN,
  setAntwoorden = NIETS,
}: {
  klant: GekozenKlant;
  dierIds: number[];
  setDierIds: Dispatch<SetStateAction<number[]>>;
  periode: Periode;
  verblijf: Record<number, DierVerblijf>;
  setVerblijf: Dispatch<SetStateAction<Record<number, DierVerblijf>>>;
  ctx: Context;
  onKlant: () => void;
  onPeriode: () => void;
  onTerug?: () => void;
  onVolgende: () => void;
  volgendeLabel?: string;
  stap?: WizardStap;
  antwoorden?: Antwoorden;
  setAntwoorden?: Dispatch<SetStateAction<Antwoorden>>;
}) {
  const t = ctx.t as Vertaal;
  const { locale } = ctx;
  const { dieren, isFetched: dierenGeladen } = useKlantDieren(klant.id);
  const gekozen = dieren.filter((d) => dierIds.includes(d.id));
  const fotos = useAnimalPhotoUrls(gekozen.map((d) => d.id));
  const locatieUrl = useLocatieUrl();
  const typenQuery = useCustom<AccommodatieType[]>({ url: locatieUrl("pension/accommodatietypes"), method: "get" });
  const typen = Array.isArray(typenQuery.data?.data) ? typenQuery.data.data : [];
  const typenKlaar = typenQuery.isFetched || typenQuery.isError;
  const berekening: Prijsberekening =
    useCustom<{ prijsberekening: Prijsberekening }>({ url: locatieUrl("pension/prijsberekening"), method: "get" })
      .data?.data?.prijsberekening ??
    "PENSIONPRIJZEN";
  const opties = useCustom<Opties>({ url: locatieUrl("pension/verzorging/opties"), method: "get" }).data?.data;
  const tarieven = usePrijscategorieen(p.aankomst);
  const beschikbaar = useCustom<Beschikbaarheid>({
    url: beschikbaarheidUrl(p, dierIds),
    method: "get",
    queryOptions: { enabled: dierIds.length > 0 },
  }).data?.data;
  const [dicht, setDicht] = useState<number[]>([]);
  const kop = stapKop(t, stap, "verblijf");
  const verplicht = useVerplicht(stap, antwoorden, dierIds);
  const typeAan = blokAan(stap, "TYPE_VERBLIJF");
  /** Wat in de kaart van elk dier staat: de vaste blokken die aan staan en de vragen per dier. */
  const inKaart = stap.elementen.filter((e) => e.actief && (e.soort === "BLOK" || (e.soort === "VRAAG" && e.perDier)));
  const onderKaarten = stap.elementen.filter((e) => e.actief && e.soort !== "BLOK" && !(e.soort === "VRAAG" && e.perDier));

  // Een dier zonder gegevens krijgt de standaard, zodra zijn verblijfstypes er zijn.
  const zonder = gekozen.filter((d) => verblijf[d.id] == null);
  useEffect(() => {
    if (!typenKlaar || zonder.length === 0) return;
    setVerblijf((v) => {
      const uit = { ...v };
      for (const d of zonder) if (uit[d.id] == null) uit[d.id] = standaardVerblijf(d, typen);
      return uit;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typenKlaar, zonder.map((d) => d.id).join(",")]);

  // Wat het verblijf kost, met dezelfde berekening als bij het aanmaken: het tarief van het dier met de meerprijs van
  // het verblijf, of de dagprijs van het verblijf uit zijn kalender, zoals de prijsberekening van de vestiging zegt.
  // Zonder extra's en verzekering: die kiest de balie pas in de volgende stappen.
  const uitkomst = useBerekening(maakVerzoek(klant, dierIds, p, verblijf, {}, GEEN_KEUZES), p).data?.data;

  const nachten = dagenTussen(p.aankomst, p.vertrek);
  const compleet = dierenGeladen && gekozen.length > 0 && verblijfCompleet(
      gekozen,
      typeAan ? typen : [],
      verblijf,
      blokAan(stap, "TARIEF") ? tarieven : [],
    );

  return (
    <>
      <div className="nr__inhoud">
        <KlantBalk
          klant={klant}
          knop={t("nieuweReservering.periode.klantBekijken")}
          knopIcoon={<UserRound size={16} aria-hidden="true" />}
          onKnop={onKlant}
        />
        <section className="nr-periodebalk" aria-label={t("nieuweReservering.verblijf.periode")}>
          <span className="nr-rond nr-rond--groot" aria-hidden="true">
            <CalendarDays size={22} />
          </span>
          <div className="nr-periodebalk__tekst">
            <strong>{t("nieuweReservering.verblijf.periode")}</strong>
            <span className="nr-regel">
              {langeDatum(p.aankomst, locale)} {p.aankomstTijd}
              <ArrowRight size={14} aria-hidden="true" />
              {langeDatum(p.vertrek, locale)} {p.vertrekTijd} (
              {nachten === 1 ? t("nieuweReservering.periode.nacht") : t("nieuweReservering.periode.nachten", { args: [nachten] })})
            </span>
          </div>
          {beschikbaar && (
            <span className="nr-uitslag nr-uitslag--klein" data-toon={beschikbaar.beschikbaar ? "groen" : "rood"}>
              {beschikbaar.beschikbaar ? <Check size={16} aria-hidden="true" /> : <AlertTriangle size={16} aria-hidden="true" />}
              {beschikbaar.beschikbaar ? t("nieuweReservering.periode.ok") : t("nieuweReservering.periode.vol")}
            </span>
          )}
          <button type="button" className="nr-knop nr-knop--rand" onClick={onPeriode}>
            <Pencil size={16} aria-hidden="true" />
            {t("nieuweReservering.verblijf.periodeAanpassen")}
          </button>
        </section>

        <h3 className="nr-kaart__titel">{kop.titel}</h3>
        <p className="nr-kaart__uitleg">{kop.uitleg}</p>

        <div className="nr-verzorging">
          {gekozen.map((d) => {
            const v = verblijf[d.id];
            const open = !dicht.includes(d.id);
            const eigen = typenVoor(d, typen);
            const type = eigen.find((x) => x.id === v?.typeId);
            const foto = fotos[String(d.id)];
            const jaren = leeftijd(d.geboortedatum, ctx);
            const naam = d.naam ?? String(d.id);
            const zet = (deel: Partial<DierVerblijf>) =>
              setVerblijf((x) => (x[d.id] ? { ...x, [d.id]: { ...x[d.id], ...deel } } : x));

            const vak = (e: WizardElement): ReactNode => {
              if (!v) return null;
              const titel = blokTitel(t, "VERBLIJF", e);
              if (e.soort === "VRAAG") {
                const sleutel = antwoordSleutel(e.id, d.id);
                const w = antwoorden[sleutel] ?? "";
                return (
                  <div key={e.id} className="nr-verzorgkaart__vak">
                    <VraagVeld
                      vraag={e}
                      label={e.titel ?? ""}
                      waarde={w}
                      onChange={(waarde) => setAntwoorden((x) => ({ ...x, [sleutel]: waarde }))}
                      t={t}
                      fout={verplicht.geprobeerd && e.verplicht && (w.trim() === "" || w === "[]")}
                    />
                  </div>
                );
              }
              switch (e.blok) {
                case "TARIEF": {
                  const eigenTarieven = tarievenVoor(d, tarieven);
                  const nodig = tariefNodig(d, tarieven);
                  return (
                    <div key={e.id} className="nr-verzorgkaart__vak">
                      <Veld label={titel} verplicht={nodig}>
                        <select
                          className="nr-invoer"
                          value={v.prijscategorieId ?? ""}
                          onChange={(ev) => zet({ prijscategorieId: ev.target.value === "" ? null : Number(ev.target.value) })}
                        >
                          <option value="">{t(nodig ? "nieuweReservering.verblijf.kiesTariefNodig" : "nieuweReservering.verblijf.kiesTarief")}</option>
                          {eigenTarieven.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.naam}
                              {o.bedrag != null ? ` — ${formatEuro(o.bedrag, locale)}` : ""}
                            </option>
                          ))}
                        </select>
                      </Veld>
                      {e.uitleg && <span className="nr-klein">{e.uitleg}</span>}
                      {eigenTarieven.length === 0 && <span className="nr-klein">{t("nieuweReservering.verblijf.geenTarieven")}</span>}
                      {nodig && v.prijscategorieId == null && (
                        <span className="nr-klein" role="status">{t("nieuweReservering.verblijf.tariefNodig", { args: [d.naam] })}</span>
                      )}
                      {(() => {
                        const gekozenTarief = eigenTarieven.find((o) => o.id === v.prijscategorieId);
                        return gekozenTarief?.bedrag != null ? (
                          <span className="nr-chip" data-toon="groen">
                            {t("nieuweReservering.verblijf.dagprijs", { args: [formatEuro(gekozenTarief.bedrag, locale)] })}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  );
                }
                case "TYPE_VERBLIJF":
                  return (
                    <div key={e.id} className="nr-verzorgkaart__vak">
                      {eigen.length === 0 ? (
                        <>
                          <span className="nr-veld__label">{titel}</span>
                          <p className="nr-klein">
                            {t("nieuweReservering.verblijf.geenTypes", { args: [meervoud(d.diersoort).toLowerCase()] })}
                          </p>
                        </>
                      ) : (
                        <Veld label={titel} verplicht>
                          <select
                            className="nr-invoer"
                            value={v.typeId ?? ""}
                            onChange={(ev) => zet({ typeId: ev.target.value === "" ? null : Number(ev.target.value) })}
                          >
                            <option value="">{t("nieuweReservering.verblijf.kiesType")}</option>
                            {eigen.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.naam}
                              </option>
                            ))}
                          </select>
                        </Veld>
                      )}
                      {e.uitleg && <span className="nr-klein">{e.uitleg}</span>}
                      {type && (
                        <div className="nr-type">
                          {type.foto ? (
                            <img className="nr-type__foto" src={type.foto} alt="" />
                          ) : (
                            <span className="nr-type__foto nr-type__foto--icoon" aria-hidden="true">
                              <AccommodatieIcoon type={type} size={34} />
                            </span>
                          )}
                          <strong>{type.naam}</strong>
                          {type.omschrijving && <span className="nr-klein">{type.omschrijving}</span>}
                          {type.prijs && type.prijs.bedrag > 0 && (
                            <span className="nr-chip" data-toon="groen">
                              {t(berekening === "ACCOMMODATIE" ? "nieuweReservering.verblijf.dagprijs" : "nieuweReservering.verblijf.meerprijs", {
                                args: [formatEuro(type.prijs.bedrag, locale)],
                              })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                case "VOEDING":
                  return (
                    <div key={e.id} className="nr-verzorgkaart__vak">
                      <h5>{titel}</h5>
                      {e.uitleg && <span className="nr-klein">{e.uitleg}</span>}
                      <Veld label={t("nieuweReservering.verblijf.typeVoeding")} verplicht>
                        <select className="nr-invoer" value={v.voeding} onChange={(ev) => zet({ voeding: ev.target.value })}>
                          <option value="STANDAARD">{t("nieuweReservering.verblijf.voedingSTANDAARD")}</option>
                          <option value="EIGEN">{t("nieuweReservering.verblijf.voedingEIGEN")}</option>
                          {(opties?.voeding ?? []).length > 0 && (
                            <optgroup label={t("nieuweReservering.verblijf.voedingPension")}>
                              {opties!.voeding.map((o) => (
                                <option key={o.id} value={`categorie:${o.id}`}>
                                  {o.naam}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </Veld>
                      <Veld label={t("nieuweReservering.verblijf.voedingInstructies")}>
                        <textarea
                          className="nr-invoer"
                          maxLength={TEKST_MAX}
                          placeholder={t("nieuweReservering.verblijf.voedingPlaats")}
                          value={v.voedingInstructies}
                          onChange={(ev) => zet({ voedingInstructies: ev.target.value })}
                        />
                      </Veld>
                      <span className="nr-teller" aria-hidden="true">
                        {v.voedingInstructies.length}/{TEKST_MAX}
                      </span>
                    </div>
                  );
                case "MEDICATIE":
                  return (
                    <div key={e.id} className="nr-verzorgkaart__vak">
                      <h5>{titel}</h5>
                      {e.uitleg && <span className="nr-klein">{e.uitleg}</span>}
                      <Veld label={t("nieuweReservering.verblijf.medicatieNodig")}>
                        <select
                          className="nr-invoer"
                          value={v.medicatie ? "ja" : "nee"}
                          onChange={(ev) => zet({ medicatie: ev.target.value === "ja" })}
                        >
                          <option value="nee">{t("nieuweReservering.verblijf.nee")}</option>
                          <option value="ja">{t("nieuweReservering.verblijf.ja")}</option>
                        </select>
                      </Veld>
                      <Veld label={t("nieuweReservering.verblijf.medicatieInstructies")}>
                        <textarea
                          className="nr-invoer"
                          maxLength={TEKST_MAX}
                          disabled={!v.medicatie}
                          placeholder={t("nieuweReservering.verblijf.medicatiePlaats")}
                          value={v.medicatieInstructies}
                          onChange={(ev) => zet({ medicatieInstructies: ev.target.value })}
                        />
                      </Veld>
                      <span className="nr-teller" aria-hidden="true">
                        {v.medicatieInstructies.length}/{TEKST_MAX}
                      </span>
                    </div>
                  );
                case "VERZORGING":
                  return (
                    <div key={e.id} className="nr-verzorgkaart__vak">
                      <h5>{titel}</h5>
                      {e.uitleg && <span className="nr-klein">{e.uitleg}</span>}
                      <Veld label={t("nieuweReservering.verblijf.borstelen")}>
                        <select className="nr-invoer" value={v.borstelen} onChange={(ev) => zet({ borstelen: ev.target.value })}>
                          <option value="">{t("nieuweReservering.verblijf.nee")}</option>
                          {(opties?.borstelen ?? []).map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.naam}
                            </option>
                          ))}
                        </select>
                      </Veld>
                      <Veld label={t("nieuweReservering.verblijf.wassen")}>
                        <select className="nr-invoer" value={v.wassen} onChange={(ev) => zet({ wassen: ev.target.value })}>
                          <option value="">{t("nieuweReservering.verblijf.nee")}</option>
                          {(opties?.wassen ?? []).map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.naam}
                            </option>
                          ))}
                        </select>
                      </Veld>
                      <Veld label={t("nieuweReservering.verblijf.samen")}>
                        <select
                          className="nr-invoer"
                          value={v.samenMetGezin ? "ja" : "nee"}
                          onChange={(ev) => zet({ samenMetGezin: ev.target.value === "ja" })}
                        >
                          <option value="nee">{t("nieuweReservering.verblijf.nee")}</option>
                          <option value="ja">{t("nieuweReservering.verblijf.jaGraag")}</option>
                        </select>
                      </Veld>
                    </div>
                  );
                case "BIJZONDERHEDEN":
                  return (
                    <div key={e.id} className="nr-verzorgkaart__vak">
                      <Veld label={titel}>
                        <textarea
                          className="nr-invoer nr-invoer--hoog"
                          maxLength={TEKST_MAX}
                          placeholder={e.uitleg ?? t("nieuweReservering.verblijf.bijzonderhedenPlaats")}
                          value={v.bijzonderheden}
                          onChange={(ev) => zet({ bijzonderheden: ev.target.value })}
                        />
                      </Veld>
                      <span className="nr-teller" aria-hidden="true">
                        {v.bijzonderheden.length}/{TEKST_MAX}
                      </span>
                    </div>
                  );
                default:
                  return null;
              }
            };

            return (
              <section key={d.id} className="nr-verzorgkaart" aria-label={naam}>
                <header className="nr-verzorgkaart__kop">
                  {foto ? (
                    <img className="nr-mini__foto" src={foto} alt="" />
                  ) : (
                    <span className="nr-mini__foto nr-dier__foto--leeg" aria-hidden="true">
                      <PawPrint size={22} />
                    </span>
                  )}
                  <div className="nr-dier__tekst">
                    <strong className="nr-dier__naam">{naam}</strong>
                    <span className="nr-dier__soort">
                      {geslachtTeken(d.geslacht)} {[d.diersoort, d.ras].filter(Boolean).join(" · ")}
                    </span>
                    <span className="nr-dier__soort">{[d.geslacht, jaren].filter(Boolean).join(" · ")}</span>
                  </div>
                  <button
                    type="button"
                    className="nr-knop nr-knop--rand"
                    disabled={dierIds.length <= 1}
                    aria-label={t("nieuweReservering.verblijf.verwijderAria", { args: [naam] })}
                    onClick={() => setDierIds((x) => x.filter((i) => i !== d.id))}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    {t("nieuweReservering.verblijf.verwijder")}
                  </button>
                  <button
                    type="button"
                    className="nr__kopknop"
                    aria-expanded={open}
                    aria-label={t(open ? "nieuweReservering.verblijf.inklappen" : "nieuweReservering.verblijf.uitklappen", { args: [naam] })}
                    onClick={() => setDicht((x) => (open ? [...x, d.id] : x.filter((i) => i !== d.id)))}
                  >
                    {open ? <ChevronUp size={20} aria-hidden="true" /> : <ChevronDown size={20} aria-hidden="true" />}
                  </button>
                </header>
                {open && !v && <p className="nr-kaart__uitleg">{t("common.loading")}</p>}
                {open && v && inKaart.length > 0 && <div className="nr-verzorgkaart__velden">{inKaart.map(vak)}</div>}
                {open && v && (
                  <VerblijfPrijs
                    prijs={Array.isArray(uitkomst?.dieren) ? uitkomst.dieren.find((x) => x.dierId === d.id) : undefined}
                    berekening={berekening}
                    t={t}
                    locale={locale}
                  />
                )}
              </section>
            );
          })}
        </div>
        <EigenElementen
          elementen={onderKaarten}
          dieren={gekozen}
          antwoorden={antwoorden}
          setAntwoorden={setAntwoorden}
          t={t}
          geprobeerd={verplicht.geprobeerd}
        />
        <VerplichtMelding open={verplicht.open} geprobeerd={verplicht.geprobeerd} t={t} />
      </div>
      <Voet
        links={
          <Terug
            t={t}
            onClick={onTerug ?? onPeriode}
            label={onTerug ? undefined : t("nieuweReservering.verblijf.terug")}
          />
        }
        rechts={
          <Volgende
            t={t}
            disabled={!compleet}
            onClick={verplicht.verder(onVolgende)}
            label={volgendeLabel ?? t("nieuweReservering.verblijf.volgende")}
          />
        }
      />
    </>
  );
}

/** Geen keuzes uit de laatste stap: de prijs van het verblijf hangt er niet van af. */
const GEEN_KEUZES = { annuleringsverzekering: false, overigeWensen: "", interneNotitie: "" };

/**
 * Wat het verblijf van dit dier kost, en waarom.
 *
 * <p>Uit de berekening van de api en niet hier nagerekend: het tarief, de meerprijs van het verblijf, het
 * hoogseizoen en de dagprijzen uit de kalender zitten daar al in, en een tweede som in het scherm zou vroeg of laat
 * iets anders zeggen dan de reservering die eruit komt.
 */
function VerblijfPrijs({
  prijs,
  berekening,
  t,
  locale,
}: {
  prijs: Berekening["dieren"][number] | undefined;
  berekening: Prijsberekening;
  t: Vertaal;
  locale: string;
}) {
  if (!prijs) return null;
  const euro = (n: number) => formatEuro(n, locale);
  const delen: string[] = [];
  if (prijs.dagenLaag > 0) delen.push(t("nieuweReservering.verblijf.prijsDagen", { args: [prijs.dagenLaag, euro(prijs.dagprijsLaag)] }));
  if (prijs.dagenHoog > 0) {
    delen.push(t("nieuweReservering.verblijf.prijsDagenHoog", { args: [prijs.dagenHoog, euro(prijs.dagprijsHoog)] }));
  }
  if (prijs.toeslag !== 0) {
    delen.push(
      berekening === "ACCOMMODATIE"
        ? t("nieuweReservering.verblijf.prijsKalender", { args: [euro(prijs.toeslag)] })
        : t("nieuweReservering.verblijf.prijsMeerprijs", { args: [prijs.accommodatie ?? "", euro(prijs.toeslag)] }),
    );
  }
  return (
    <footer className="nr-verzorgkaart__prijs" data-testid="verblijf-prijs">
      <div>
        <strong>{t("nieuweReservering.verblijf.prijsTitel")}</strong>
        <span className="nr-klein">
          {t(berekening === "ACCOMMODATIE" ? "nieuweReservering.verblijf.prijsUitAccommodatie" : "nieuweReservering.verblijf.prijsUitTarief")}
          {delen.length > 0 ? " · " + delen.join(" + ") : ""}
        </span>
      </div>
      <strong className="nr-verzorgkaart__bedrag">{euro(prijs.bedrag)}</strong>
    </footer>
  );
}
